import { api } from "../../lib";

export type ReflexSortField = "name" | "updatedAt" | "versionNumber";
export type ReflexSortOrder = "asc" | "desc";

export interface ReflexPanelOption {
  id: string;
  name: string;
}

export interface ReflexIcdOption {
  id: string;
  code: string;
  description: string;
}

export interface ReflexRule {
  id: string;
  targetPanelDefinitionId: string;
  displaySequence: number;
  targetPanel: ReflexPanelOption;
  icdCodes: ReflexIcdOption[];
}

export interface ReflexDefinition {
  id: string;
  name: string;
  description: string | null;
  isActive: boolean;
  versionNumber: number;
  startingPanelDefinitionId: string;
  startingPanel: ReflexPanelOption;
  rules: ReflexRule[];
  createdAt: string;
  updatedAt: string;
}

export interface SaveReflexInput {
  name: string;
  description?: string;
  startingPanelDefinitionId: string;
  rules: Array<{ targetPanelDefinitionId: string; icdCodeIds: string[] }>;
}

export interface ReflexListParams {
  page: number;
  limit: number;
  search?: string;
  status?: "active" | "inactive";
  sortBy: ReflexSortField;
  sortOrder: ReflexSortOrder;
}

export interface PaginatedReflexes {
  data: ReflexDefinition[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface ReflexAdminOptions {
  panels: ReflexPanelOption[];
  icdCodes: ReflexIcdOption[];
}

export class ReflexAdminRequestError extends Error {}

function responseStatus(error: unknown): number | undefined {
  if (typeof error !== "object" || error === null || !("response" in error))
    return undefined;
  return (error as { response?: { status?: number } }).response?.status;
}

function translateError(error: unknown): ReflexAdminRequestError {
  const status = responseStatus(error);
  if (status === 403)
    return new ReflexAdminRequestError(
      "You do not have permission to manage reflexes."
    );
  if (status === 409)
    return new ReflexAdminRequestError(
      "This reflex conflicts with the current configuration. Refresh and try again."
    );
  if (status === 400 || status === 422)
    return new ReflexAdminRequestError(
      "Review the starting panel, follow-on panels, and ICD-10 rules."
    );
  return new ReflexAdminRequestError(
    "Reflex configuration is unavailable right now. Refresh and try again."
  );
}

async function translated<T>(request: Promise<T>): Promise<T> {
  try {
    return await request;
  } catch (error) {
    throw translateError(error);
  }
}

function list(params: ReflexListParams): Promise<PaginatedReflexes> {
  const query = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== "") query.set(key, String(value));
  });
  return translated(
    api.get<PaginatedReflexes>(`/admin/reflexes?${query.toString()}`)
  );
}

export function getReflexAdminErrorMessage(error: unknown): string {
  return error instanceof ReflexAdminRequestError
    ? error.message
    : "Reflex configuration is unavailable right now. Refresh and try again.";
}

export const reflexesService = {
  list,
  getOptions: () =>
    translated(api.get<ReflexAdminOptions>("/admin/reflexes/options")),
  create: (input: SaveReflexInput) =>
    translated(api.post<ReflexDefinition>("/admin/reflexes", input)),
  createVersion: (id: string, input: SaveReflexInput) =>
    translated(api.put<ReflexDefinition>(`/admin/reflexes/${id}`, input)),
  updateStatus: (id: string, isActive: boolean) =>
    translated(
      api.patch<ReflexDefinition>(`/admin/reflexes/${id}/status`, { isActive })
    ),
};
